import { Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useUpdateWithNewElementsActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useUpdateWithNewElementsActionPlan.tsx";
import { ActionPropertyQuestions } from "./ActionPropertyQuestions";
import CreateOutlined from "@mui/icons-material/CreateOutlined";
import { ActionCalculationFields } from "../ActionCalculationFields";
import { useMyActionPlanContext } from "../../../context/useMyActionPlan";

type ActionCalculationMultilevelProps = unknown;

export const ActionCalculationMultilevel = (props: ActionCalculationMultilevelProps) => {
    return <ActionCalculationMultilevelWithData />;
};

type ActionCalculationMultilevelWithDataProps = unknown;

const ActionCalculationMultilevelWithData = (props: ActionCalculationMultilevelWithDataProps) => {
    const theme = useTheme();
    const { isEditMode } = useMyActionPlanContext();

    return (
        <Stack gap={3}>
            <Stack
                gap={3}
                sx={{
                    padding: theme.spacing(2),
                    backgroundColor: "#FFFFFF",
                    borderRadius: "12px",
                    border: `1px solid ${theme.custom.grey[400]}`,
                    boxShadow: "0px 2px 8px 0px rgba(42, 43, 39, 0.04)",
                }}
            >
                <ActionCalculationMultilevelHeader />

                <Stack gap={3} sx={{ paddingBottom: theme.spacing(1) }}>
                    <ActionPropertyQuestions />
                </Stack>

                {isEditMode === true && <ActionCalculationMultilevelFooter />}
            </Stack>

            <ActionCalculationFields />
        </Stack>
    );
};

const ActionCalculationMultilevelHeader = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { isEditMode, setIsEditMode } = useMyActionPlanContext();

    return (
        <Stack>
            <Stack direction="row" alignItems="center" gap={theme.spacing(2)} sx={{ paddingBottom: theme.spacing(2) }}>
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: theme.spacing(4.5),
                        height: theme.spacing(4.5),
                        background: theme.palette.primary[50],
                        color: theme.palette.primary[600],
                        borderRadius: theme.spacing(0.5),
                    }}
                >
                    <FoodsIcon size={4} icon="diagnostic" />
                </Stack>

                <Stack flex={1}>
                    <Typography variant="h3">
                        {t(
                            "action_plan.multilevel.refinement_questions.title",
                            "Precision questions about your current diagnostic",
                        )}
                    </Typography>
                    {isEditMode === false ?
                        <Stack
                            flexDirection="row"
                            alignItems="center"
                            gap={theme.spacing(1)}
                            sx={{ color: theme.custom.green[600] }}
                        >
                            <FoodsIcon size={2} icon="check" />
                            <Typography variant="body" sx={{ fontWeight: 600, color: "inherit" }}>
                                {t("data_validated", "Data validated")}
                            </Typography>
                        </Stack>
                    :   <Typography variant="body">
                            {t(
                                "action_plan.multilevel.refinement_questions.description",
                                "Fill in and confirm this data to simulate the impact of this action.",
                            )}
                        </Typography>
                    }
                </Stack>

                {isEditMode === false && (
                    <Button variant="secondary" startIcon={<CreateOutlined />} onClick={() => setIsEditMode(true)}>
                        {t("modifier")}
                    </Button>
                )}
            </Stack>

            <Divider />
        </Stack>
    );
};

const ActionCalculationMultilevelFooter = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { areQuestionsAnswered, setIsEditMode } = useMyActionPlanContext();
    const { actionPlan, savingStatus } = useActionsContext();

    const reinitialize = useUpdateWithNewElementsActionPlan(actionPlan?.id);
    const isConfirmationDisabled = !areQuestionsAnswered || savingStatus.isSaving;

    return (
        <Stack>
            <Divider />

            <Stack sx={{ paddingTop: theme.spacing(2), alignItems: "flex-start" }}>
                <Button
                    variant="contained"
                    onClick={() => {
                        savingStatus.setSaving(true);

                        reinitialize.mutate(null, {
                            onSuccess: () => {
                                savingStatus.setSaving(false);

                                setIsEditMode(false);
                            },
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                    disabled={isConfirmationDisabled}
                    startIcon={<FoodsIcon size={2} icon="check" />}
                    sx={{ color: "#FFFFFF" }}
                >
                    {t("action_plan.multilevel.confirm", "Confirm and proceed to the simulation")}
                </Button>
            </Stack>
        </Stack>
    );
};
